import React, { useEffect, useState } from "react";
import { Redirect } from "react-router-dom";
import { useOktaAuth } from "@okta/okta-react";
import { useUserInfo } from "../../providers/userProvider";
import cookie from "js-cookie";
import { SurveyPage } from "./SurveyPage";
import { getAvailableLanguages, getSurveyById, getSurveyLiterals } from "../../services/survey.service";
import { getCampaigsByRn } from "../../services/campaign.service";
import { getSuppSurveyConfiguration } from "../../services/userService";
import { ALL, CODE_SURVEY_LANGUAGE, DEFAULT_LANGUAGE, DISCLAIMER_MSG_POSITION, SCBM_USER_TYPE } from "../../utils/surveyLiterals";
import { ERR_DESC_REQUEST_NUMBER, ERR_TITLE_REQUEST_NUMBER, ERR_TITLE_REQUEST_NUMBER_PRIVATE, ERR_DESC_REQUEST_NUMBER_PRIVATE, ERR_FORBIDDEN_PRIVATE } from "../../utils/errorLiterals";
import { useLocation } from 'react-router'
import { CRS_EXT_TOKEN, CRS_EXT_PARAM, CRS_EXT_TOKEN_RENEWAL_INTERVAL, CRS_EXT_TOKEN_RENEWAL_INTERVAL_ON_ERROR } from "../../utils/constants";
import { renewToken } from "../../services/auth";

function isValidRN(rn) {
  return rn && rn.length <= 19 && !isNaN(rn) && Number.isInteger(parseFloat(rn)); //Size in DB
}

function validationRules(respCampaign, userInfo) {
  let error;
  if (respCampaign) {
    let campaign = respCampaign;
    if (userInfo.userType !== SCBM_USER_TYPE.SALARY) {
      //it is normal user, we have to validate that the current user has the supplier
      if (!userInfo.suppliers || userInfo.suppliers.length <= 0 || !userInfo.suppliers.includes(campaign.assignments[0].supplierNumber)) {
        error = "No suppliers assigned";
      } //else everything is OK
    } //else Read only for the deere employee
  } else {
    //The assignment does not exist
    error = "No campaign found";
  }
  return error;
}

export default function HomePage(props) {
  const actualURL = window.location.href;
  const { authState } = useOktaAuth();
  const userInfo = useUserInfo();
  const [campaignAssigned, setCampaignAssigned] = useState();
  const [isForbidden, setIsForbidden] = useState(false);
  const [containsError, setContainsError] = useState(false);
  const [survey, setSurvey] = useState(undefined);
  const [timeInterval, setTimeInterval] = useState(CRS_EXT_TOKEN_RENEWAL_INTERVAL); // 50 minutes
  const [currentLanguageCode] = useState(cookie.get("i18next") || DEFAULT_LANGUAGE);
  const [literals, setLiterals] = useState([]);
  const [surveyLanguages, setSurveyLanguages] = useState([]);
  const [disclaimerPosition, setDisclaimerPosition] = useState("");
  const location = useLocation();

  let value = Object.fromEntries(new URLSearchParams(location.search));
  let rn = value.rn;

  const [nonOktaParams] = useState({
    previous: location && location.state && location.state.previous ? location.state.previous : "",
    queryParam: location && location.state && location.state.queryParam ? location.state.queryParam : "",
    isAuthNonOkta: location && location.state && location.state.isAuthNonOkta ? location.state.isAuthNonOkta : false,
  });
  if (nonOktaParams && nonOktaParams.previous === "/surveyLogin" && !rn) {
    rn = nonOktaParams.queryParam.toString();
  }
  let isAuthMethodOkta = authState && authState.isAuthenticated ? true : false;
  let nonOktaAuthFailed = !isAuthMethodOkta ? (nonOktaParams && nonOktaParams.isAuthNonOkta ? false : true) : false;
  let token = isAuthMethodOkta ? authState.accessToken.accessToken : sessionStorage.getItem(CRS_EXT_TOKEN);

  useEffect(() => {
    if (window.location.pathname === "/surveyext") props.setShowHeaderFooter(false);
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem(CRS_EXT_TOKEN)) {
      const updateToken = async() => {
        const tmpParam = sessionStorage.getItem(CRS_EXT_PARAM);
        
        try {
          await renewToken(tmpParam);
          console.log("Token renewed at: " + new Date().toLocaleString());
          if (timeInterval !== CRS_EXT_TOKEN_RENEWAL_INTERVAL) {
            setTimeInterval(CRS_EXT_TOKEN_RENEWAL_INTERVAL); // Reset interval to 50 minutes on successful token renewal
          }
        } catch (error) {
          console.error("Error renewing token: ", error.message);
          setTimeInterval(CRS_EXT_TOKEN_RENEWAL_INTERVAL_ON_ERROR); // Set interval to 1 minute on error
        }        
      };

      const tokenRenewalInterval = setInterval(updateToken, timeInterval);
      return () => clearInterval(tokenRenewalInterval);
    }
  }, [timeInterval]);



  useEffect(() => {
    if ((!isAuthMethodOkta || userInfo) && rn && !campaignAssigned && isValidRN(rn)) {
      getCampaigsByRn(rn, token)
        .then((res) => {
          let errorValidation = isAuthMethodOkta ? validationRules(res, userInfo) : false;
          if (errorValidation) {
            console.log(errorValidation);
            setContainsError(true);
          } else {
            setCampaignAssigned(res);
          }
        })
        .catch((error) => {
          console.error(error.message);
          const errorMsg = error.message ? error.message.toString().trim() : "";
          if (errorMsg.toLowerCase() === 'forbidden' || errorMsg === ERR_FORBIDDEN_PRIVATE) {
            console.log('Setting forbidden to true');
            setIsForbidden(true);
          }else{
            setContainsError(true);
          }
        });
    }
  }, [userInfo, campaignAssigned]);

  useEffect(() => {
    if (!survey && campaignAssigned && !containsError) {
      if (currentLanguageCode !== DEFAULT_LANGUAGE) {
        getSuppSurveyConfiguration(campaignAssigned.surveyId, currentLanguageCode, token, isAuthMethodOkta).then((s) => {
          setSurvey(s);
        });
      } else {
        getSurveyById(campaignAssigned.surveyId, token).then((s) => {
          setSurvey(s);
        });
      }
    }
  }, [survey, campaignAssigned]);

  useEffect(async () => {
    if (survey && !containsError) {
      try {
        const languageList = await getAvailableLanguages(token);
        const literalsData = await getSurveyLiterals(survey.id, null, token, isAuthMethodOkta);
        const languages = [];
        const literals = [];
        literalsData.forEach((lt) => {
          if (lt.code.toLowerCase() === `${survey.code.toLowerCase()}_${CODE_SURVEY_LANGUAGE}`) {
            const index = languageList.findIndex((item) => item.languageCd.split("_")[0] === lt.languageCd.split("_")[0]);
            if (index > -1) languages.push({ code: lt.languageCd, name: lt.text, country_code: languageList[index].countryCode });
          }
          else if (lt.languageCd.split("_")[0] === currentLanguageCode) literals.push(lt);
          else if (lt.languageCd === ALL && lt.code === DISCLAIMER_MSG_POSITION) setDisclaimerPosition(lt.text);
        });
        setSurveyLanguages(languages);
        setLiterals(literals);
      } catch (err) {
        console.log(err);
      }
    }
  }, [survey]);

  if(isForbidden){
    return <Redirect
      to={{
        pathname: "/error",
        state: {
          errorTitle: ERR_TITLE_REQUEST_NUMBER_PRIVATE,
          errorDescription: ERR_DESC_REQUEST_NUMBER_PRIVATE,
            errorUrl: actualURL,
            errorUser: userInfo ? userInfo.userID : "",
          },
        }}
    />
  } 

  if (!rn || containsError || !isValidRN(rn) || (!isAuthMethodOkta && nonOktaAuthFailed)) {
    return (
      <Redirect
        to={{
          pathname: "/error",
          state: {
            errorTitle: ERR_TITLE_REQUEST_NUMBER,
            errorDescription: ERR_DESC_REQUEST_NUMBER,
            errorUrl: actualURL,
            errorUser: userInfo ? userInfo.userID : "",
          },
        }}
      />
    );
  }
  if ((isAuthMethodOkta && !userInfo) || !campaignAssigned || !survey) {
    return (
      <div>
        <h5>Loading...</h5>
      </div>
    );
  } else {
    return (
      <SurveyPage
        survey={survey}
        setSurvey={setSurvey}
        campaignAssigned={campaignAssigned}
        literals={literals}
        setLiterals={setLiterals}
        isAuthMethodOkta={isAuthMethodOkta}
        token={token}
        surveyLanguages={surveyLanguages}
        disclaimerPosition={disclaimerPosition}
      />
    );
  }
}
